import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { ChevronLeft, Loader2, GitCompareArrows } from "lucide-react";
import StatementLedger from "@/components/StatementLedger";
import StatementLedgerDiff from "@/components/StatementLedgerDiff";
import PrintPageButton from "@/components/PrintPageButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Dedicated printable page for a single statement's ledger.
 *
 * Route: /statements/:id/ledger
 *
 * Reuses <StatementLedger> in `variant="page"` mode so the running-balance
 * table isn't clipped to a modal height. Sits under AppLayout so it inherits
 * the sidebar / breadcrumb navigation.
 */
export default function StatementLedgerPage() {
    const { id } = useParams();
    const [stmt, setStmt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [diffOpen, setDiffOpen] = useState(false);

    useEffect(() => {
        api.get("/statements")
            .then((r) => {
                const found = (r.data || []).find((s) => s.id === id);
                setStmt(found || null);
            })
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="p-10 flex items-center gap-2 text-stone-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
    if (!stmt) return <div className="p-10 text-stone-500">Statement not found. <Link to="/statements" className="underline">Back to Statements</Link></div>;

    return (
        <div className="space-y-5" data-testid="ledger-page">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3 no-print">
                <div>
                    <Link to="/statements" className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1" data-testid="ledger-page-back">
                        <ChevronLeft size={12} /> Statements
                    </Link>
                    <div className="eyebrow mt-2">Statement-period ledger</div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-1">
                        {stmt.bank_name} · •••• {stmt.last_4}
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        Statement month <span className="font-mono-figure text-stone-800">{stmt.statement_month}</span>
                        {stmt.statement_date && <> · Stmt date <span className="font-mono-figure text-stone-800">{fmtDate(stmt.statement_date)}</span></>}
                        {stmt.due_date && <> · Due <span className="font-mono-figure text-stone-800">{fmtDate(stmt.due_date)}</span></>}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDiffOpen(true)}
                        data-testid="ledger-diff-open"
                        className="gap-1.5"
                    >
                        <GitCompareArrows size={13} /> Compare with previous
                    </Button>
                    <PrintPageButton label="Print / PDF" />
                </div>
            </div>

            <StatementLedger statementId={id} variant="page" />

            <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Ledger diff — month-over-month check</DialogTitle>
                    </DialogHeader>
                    <StatementLedgerDiff statementId={id} onClose={() => setDiffOpen(false)} />
                </DialogContent>
            </Dialog>
        </div>
    );
}
